import {
  Box,
  Flex,
  Text,
  useColorStyle,
  useColorMode,
  theme,
  Spinner,
} from '@tonic-one/react';

import { useState, useMemo } from 'react';
import MenuWithLabel from '@Components/Common/Menu';
import { useTranslation } from 'react-i18next';
import { useOrgStore } from '@Models/useOrgModel';
import { OperationsApi } from '@Apis';
import {
  FeedbackFilterCategory,
  FeedbackStatisticView,
  EuqTimeRangeEnum,
} from '../../type';
import { FeedbackCategoryStrMap } from '../../constants';
import { getUtcTimestamp } from 'src/utils/time';
import { useUtcOffsetStore } from '@Models/useUtcOffsetModel';
import { useQuery } from '@tanstack/react-query';
import { ECharts } from 'src/ECharts';
import { EChartsOption, LineSeriesOption, PieSeriesOption } from 'echarts';
import useChartDateFormatter from '@Dashboard/RiskUser/useChartDateFormatter';
import { baseLineSeriesOption } from 'src/ECharts/utils';
import merge from 'lodash/merge';

const COLORS: Record<string, string> = {
  spam: '#578AEF',
  phishing: '#FF7633',
  notspam: '#04C45A',
};

const CHART_HEIGHT = 250;

export default function EuqChart({
  timeRange,
}: {
  timeRange: EuqTimeRangeEnum;
}) {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation(['operations', 'common']);
  const { offset } = useUtcOffsetStore();
  const { orgId } = useOrgStore();
  const { format } = useChartDateFormatter();

  const [viewChar, setViewChar] = useState<FeedbackStatisticView>(
    FeedbackStatisticView.LineChart
  );

  const viewCharMenuItems = useMemo(() => {
    const viewMap = {
      [FeedbackStatisticView.LineChart]: t('operations.euq.view.LineChart'),
      [FeedbackStatisticView.PieChart]: t('operations.euq.view.pieChart'),
    };
    return [
      FeedbackStatisticView.LineChart,
      FeedbackStatisticView.PieChart,
    ].map((item) => {
      return {
        key: item,
        value: viewMap[item],
      };
    });
  }, [t]);

  const { data, isLoading, isPending } = useQuery({
    queryKey: ['euqFeedbackStatistic', timeRange, orgId],
    queryFn: () =>
      OperationsApi.apiGetEuqFeedbackStatistic({
        predefined: timeRange,
        dimension: 'category',
      }),
    select: (data) => data.data,
  });

  const { pieSeriesData, lineSeries, lineSeriesTime, dataCount, legendData } =
    useMemo(() => {
      const legendData: Record<string, number> = {
        [t(FeedbackCategoryStrMap[FeedbackFilterCategory.Spam])]: 0,
        [t(FeedbackCategoryStrMap[FeedbackFilterCategory.Phishing])]: 0,
        [t(FeedbackCategoryStrMap[FeedbackFilterCategory.NotSpam])]: 0,
      };
      const lineSeriesTime: Record<string, number[]> = {
        [t(FeedbackCategoryStrMap[FeedbackFilterCategory.Spam])]: [],
        [t(FeedbackCategoryStrMap[FeedbackFilterCategory.Phishing])]: [],
        [t(FeedbackCategoryStrMap[FeedbackFilterCategory.NotSpam])]: [],
      };

      if (!data) {
        return {
          pieSeriesData: [],
          lineSeries: [],
          lineSeriesTime,
          dataCount: 0,
          legendData,
        };
      }

      let dataCount = 0;
      const pieSeriesData: PieSeriesOption['data'] = [];
      const lineSeries: LineSeriesOption[] = [];
      const categories: FeedbackFilterCategory[] = [
        FeedbackFilterCategory.Spam,
        FeedbackFilterCategory.Phishing,
        FeedbackFilterCategory.NotSpam,
      ];

      categories.map((item) => {
        let categoryCount = 0;
        const categoryLineData: number[] = [];
        if (Array.isArray(data[item]) && data[item].length) {
          let categoryData = data[item];

          if (
            timeRange === EuqTimeRangeEnum.Last1w &&
            categoryData.length > 7
          ) {
            categoryData = categoryData.slice(-7);
          } else if (
            timeRange === EuqTimeRangeEnum.Last1M &&
            categoryData.length > 30
          ) {
            categoryData = categoryData.slice(-30);
          }

          categoryData.map((i: any) => {
            categoryCount = categoryCount + i.count;
            dataCount = dataCount + i.count;
            categoryLineData.push(i.count);
            lineSeriesTime[t(FeedbackCategoryStrMap[item])].push(
              getUtcTimestamp(i.date, offset)
            );
          });
        }
        pieSeriesData.push({
          name: t(FeedbackCategoryStrMap[item]),
          value: categoryCount,
          itemStyle: {
            color: COLORS[item],
          },
        });
        lineSeries.push(
          merge(
            {
              type: 'line',
              name: t(FeedbackCategoryStrMap[item]),
              data: categoryLineData,
              color: COLORS[item],
              tooltip: {
                formatter: (params) =>
                  `${params.name}<br />${params.marker}${params.seriesName}<span style="float: right; margin-left: 20px;"><b>${params.value} (${Math.floor((legendData[params.seriesName as string] / dataCount) * 100)}%)</b></span>`,
              },
            } satisfies LineSeriesOption,
            baseLineSeriesOption
          )
        );
        legendData[t(FeedbackCategoryStrMap[item])] = categoryCount;
      });

      return {
        pieSeriesData,
        lineSeries,
        lineSeriesTime,
        dataCount,
        legendData,
      };
    }, [data, offset, t, timeRange]);

  const chartTitle: EChartsOption['title'] = useMemo(
    () => ({
      text: `{title|${t('operations.euq.summary.totalTitle')}}\n{count|${dataCount}}`,
      padding: [40, 0, 0, 0],
      textStyle: {
        rich: {
          title: {
            color: (theme.colors as any)[colorStyle.color.primary],
            fontSize: 20,
          },
          count: {
            color: (theme.colors as any)[colorStyle.color.primary],
            fontSize: 24,
            lineHeight: 32,
          },
        },
      },
    }),
    [colorStyle.color.primary, dataCount, t]
  );

  const chartLegend: EChartsOption['legend'] = useMemo(
    () => ({
      top: 'middle',
      itemGap: 50,
      itemStyle: {
        borderColor: 'transparent',
      },
      textStyle: {
        padding: [28, 0, 0, 0],
        rich: {
          name: {
            color: (theme.colors as any)[colorStyle.color.secondary],
            fontSize: 16,
            lineHeight: 24,
          },
          value: {
            color: (theme.colors as any)[colorStyle.color.primary],
            fontSize: 24,
          },
          percent: {
            color: (theme.colors as any)[colorStyle.color.primary],
            fontSize: 16,
            lineHeight: 24,
            padding: [4, 0, 0, 4],
          },
        },
      },
      formatter: (name) => {
        return `{name|${name}}\n{value|${legendData[name]}} {percent|(${Math.floor((legendData[name] / dataCount) * 100)}%)}`;
      },
    }),
    [colorStyle, dataCount, legendData]
  );

  const xData = useMemo(
    () =>
      lineSeriesTime[
        t(FeedbackCategoryStrMap[FeedbackFilterCategory.Spam])
      ].map((timestamp) => format({ timestamp, interval: timeRange as any })),
    [format, lineSeriesTime, t, timeRange]
  );

  const loading = isLoading || isPending;

  return (
    <Box>
      <Flex direction="column" border="0px solid" pb="20px">
        <Text
          fontWeight="600"
          fontSize="20px"
          lineHeight="28px"
          pl="16px"
          mb="20px"
        >
          {t('operations.euq.summary.title')}
        </Text>
        <Flex pl="16px" gap="8px">
          <MenuWithLabel
            menuItems={viewCharMenuItems}
            minWidth={200}
            onClick={(e: any) => {
              setViewChar(e.target.value as FeedbackStatisticView);
            }}
            selectedKey={viewChar}
            label={t('operations.euq.label.view')}
          />
        </Flex>
      </Flex>
      {loading ? (
        <Flex
          width="100%"
          height={CHART_HEIGHT}
          justifyContent="center"
          alignItems="center"
        >
          <Spinner m="auto" />
        </Flex>
      ) : dataCount === 0 ? (
        <Flex
          width="100%"
          height={CHART_HEIGHT}
          justifyContent="center"
          alignItems="center"
        >
          <Text color={colorStyle.color.secondary} textAlign="center">
            {t('common.noData')}
          </Text>
        </Flex>
      ) : (
        <>
          {viewChar === 'pie' && (
            <ECharts
              title={{ ...chartTitle, left: '35%' }}
              series={{
                type: 'pie',
                radius: ['50%', '70%'],
                center: ['20%', '50%'],
                itemStyle: {
                  borderWidth: 1,
                  borderColor: '#fff',
                },
                stillShowZeroSum: false,
                label: {
                  show: true,
                  position: 'center',
                  formatter: () => {
                    return `{count|${dataCount}}\n{title|${t('operations.euq.label.total')}}`;
                  },
                  rich: {
                    count: {
                      color: (theme.colors as any)[colorStyle.color.primary],
                      fontSize: 32,
                    },
                    title: {
                      color: (theme.colors as any)[colorStyle.color.primary],
                      fontSize: 16,
                      lineHeight: 24,
                    },
                  },
                  fontWeight: 500,
                },
                avoidLabelOverlap: false,
                labelLine: {
                  show: false,
                },
                showEmptyCircle: true,
                tooltip: {
                  formatter: (params) =>
                    `${t('operations.euq.detail.table.header.reportedAs')}: ${params.name}<br />${params.marker}<b>${legendData[params.name]} (${Math.floor((legendData[params.name] / dataCount) * 100)}%)</b>`,
                },
                data: pieSeriesData,
              }}
              xAxis={{
                show: false,
              }}
              legend={{ ...chartLegend, left: '35%' }}
              width={1000}
              height={CHART_HEIGHT}
              animation={false}
            />
          )}
          {viewChar === 'line' && (
            <ECharts
              title={{ ...chartTitle, left: '52%' }}
              grid={{ left: 70, bottom: 0, right: '52%', containLabel: true }}
              series={lineSeries}
              xAxis={{
                data: xData,
                axisLabel: {
                  fontSize: 12,
                  rotate: 30,
                  width: 80,
                  margin: 15,
                  overflow: 'break',
                  hideOverlap: true,
                },
                // Minimum interval for the x-axis in milliseconds
                minInterval:
                  timeRange === EuqTimeRangeEnum.Last1M ||
                  timeRange === EuqTimeRangeEnum.Last3M
                    ? 48 * 60 * 60 * 1000
                    : 0,
                axisTick: {
                  alignWithLabel: true,
                  length: 6,
                },
              }}
              yAxis={{
                axisLabel: {
                  fontSize: 12,
                },
                minInterval: 1,
              }}
              legend={{ ...chartLegend, left: '52%' }}
              height={CHART_HEIGHT}
              animation={false}
              tooltip={{
                trigger: 'axis',
              }}
            />
          )}
        </>
      )}
    </Box>
  );
}
